import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import { Reply as ReplyIcon } from '@mui/icons-material';
import { useMemo, useState } from 'react';
import { store } from '../../../../app/store';
import {
  SIMPLE_OUTBOUND_SEND_SUCCESS_MESSAGE,
  useGetConfiguredFinanceBccQuery,
  useLazyGetSimpleOutboundStatusQuery,
  useSendSimpleOutboundMutation,
  waitForSimpleOutboundResult,
  type SimpleOutboundPreview,
} from '../../api/simpleOutboundApi';
import { invalidateGridDataCache } from '../../api/invalidateGridDataCache';
import { extractErrorMessage } from '../../../../utils/errorUtils';
import { FinanceEmailComposeAttachments } from './FinanceEmailComposeAttachments';
import { FinanceEmailComposeFields } from './FinanceEmailComposeFields';
import { resolveEmailSource, type ClientEmailSource } from './financeEmailComposeUtils';
import { DRAGGABLE_DIALOG_TITLE_ID, DraggableResizablePaper } from './DraggableResizablePaper';

type SendSimpleOutboundDialogProps = {
  open: boolean;
  clientId: number;
  preview: SimpleOutboundPreview;
  onClose: () => void;
  onSuccess: (message: string) => void;
  showEmailSourceToggle?: boolean;
};

export const SendSimpleOutboundDialog = ({
  open,
  clientId,
  preview,
  onClose,
  onSuccess,
  showEmailSourceToggle = true,
}: SendSimpleOutboundDialogProps) => {
  const isReply = Boolean(preview.isReply);
  const isReplyAll = Boolean(preview.isReplyAll);
  const canToggleEmailSource = !isReply && showEmailSourceToggle;
  const [to, setTo] = useState(preview.to ?? '');
  const [cc, setCc] = useState(preview.cc ?? '');
  const [subject, setSubject] = useState(preview.subject ?? '');
  const [message, setMessage] = useState(preview.message ?? '');
  const [files, setFiles] = useState<File[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);
  const [isWaitingForResult, setIsWaitingForResult] = useState(false);

  const hasPreviewBcc = typeof preview.bcc === 'string';
  const { data: configuredBcc = '' } = useGetConfiguredFinanceBccQuery(undefined, {
    skip: !open || hasPreviewBcc,
  });
  const bcc = hasPreviewBcc ? (preview.bcc ?? '') : configuredBcc;

  const [sendEmail, sendState] = useSendSimpleOutboundMutation();
  const [fetchStatus] = useLazyGetSimpleOutboundStatusQuery();
  const isSending = sendState.isLoading || isWaitingForResult;

  const emailSource = useMemo(
    () => resolveEmailSource(to, preview.financeEmail, preview.clientEmail),
    [to, preview.financeEmail, preview.clientEmail]
  );

  const handleClose = () => {
    if (isSending) {
      return;
    }

    onClose();
  };

  const handleEmailSourceChange = (source: ClientEmailSource) => {
    const nextTo =
      source === 'finance'
        ? (preview.financeEmail ?? '').trim()
        : (preview.clientEmail ?? '').trim();
    if (nextTo) {
      setTo(nextTo);
    }
  };

  const handleSend = async () => {
    setLocalError(null);

    if (!to.trim()) {
      setLocalError('Recipient email is required.');
      return;
    }

    if (!subject.trim()) {
      setLocalError('Subject is required.');
      return;
    }

    try {
      const queued = await sendEmail({
        clientId,
        to: to.trim(),
        cc: cc.trim(),
        subject: subject.trim(),
        message,
        files,
        inReplyTo: preview.inReplyTo,
        references: preview.references,
      }).unwrap();

      setIsWaitingForResult(true);

      const result = await waitForSimpleOutboundResult(queued.historyId, async (historyId) => {
        const status = await fetchStatus(historyId).unwrap();
        return status;
      });

      if ((result.status || '').toLowerCase() === 'failed') {
        setLocalError(result.message || 'Email was not sent. Please try again or contact support');
        return;
      }

      invalidateGridDataCache(store.dispatch);
      onSuccess(result.message || SIMPLE_OUTBOUND_SEND_SUCCESS_MESSAGE);
      onClose();
    } catch (error) {
      setLocalError(
        extractErrorMessage(error, 'Email was not sent. Please try again or contact support')
      );
    } finally {
      setIsWaitingForResult(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperComponent={DraggableResizablePaper}
      aria-labelledby={DRAGGABLE_DIALOG_TITLE_ID}
    >
      <DialogTitle id={DRAGGABLE_DIALOG_TITLE_ID} sx={{ cursor: 'move', userSelect: 'none' }}>
        {isReplyAll ? 'Reply all' : isReply ? 'Reply' : 'Send email'}
        {preview.clientName ? ` — ${preview.clientName}` : ''}
      </DialogTitle>
      <DialogContent
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          paddingTop: '16px !important',
        }}
      >
        <Stack spacing={2}>
          {localError && <Alert severity="error">{localError}</Alert>}
          {isReply && (
            <Alert severity="info" icon={<ReplyIcon fontSize="inherit" />}>
              Replying to
              {preview.replyToSubject ? `: ${preview.replyToSubject}` : ' inbound email'}
            </Alert>
          )}

          <FinanceEmailComposeFields
            to={to}
            cc={cc}
            bcc={bcc}
            subject={subject}
            message={message}
            onToChange={setTo}
            onCcChange={setCc}
            onSubjectChange={setSubject}
            onMessageChange={setMessage}
            disabled={isSending}
            financeEmail={canToggleEmailSource ? preview.financeEmail : undefined}
            clientEmail={canToggleEmailSource ? preview.clientEmail : undefined}
            emailSource={canToggleEmailSource ? emailSource : null}
            onEmailSourceChange={canToggleEmailSource ? handleEmailSourceChange : undefined}
          />

          <FinanceEmailComposeAttachments files={files} onChange={setFiles} disabled={isSending} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSend()}
          disabled={isSending || !to.trim() || !subject.trim()}
          startIcon={isSending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isSending ? 'Sending...' : isReply ? 'Send reply' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendSimpleOutboundDialog;
